import { useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { checkOutboundNumber, formatPhoneNumber, type PhoneRecent } from '@avslack/shared';
import { savePhoneContact } from '../lib/api';
import {
  phoneContactsKey,
  phoneRecentKey,
  useCallableLines,
  usePhoneContacts,
  usePhoneRecent,
} from '../data/phone';
import { AssignCustomer } from './CustomerCard';
import { defaultLineId, LinePicker } from './LinePicker';
import { useUsers } from '../data/users';
import { formatDay, formatTime } from '../lib/format';
import { Icon } from './Icon';
import { useDialFromChannel } from './PhoneLegs';
import { TtDirectory } from './TtDirectory';
import { useCustomerSystem } from '../lib/session';
import { useDismiss } from '../lib/dismiss';

/**
 * A TELEFONOS CSATORNA sava a fejlec alatt.
 *
 * Bal oldalt a TARCSAZO (csak akinek van joga kifele telefonalni): szam, Hivas
 * - a hivas a kulon hivas-ablakban indul. Jobb oldalt a CIMJEGYZEK: a korabbi
 * hivott es hivo szamok, nevvel, ha van; itt lehet nevet adni egy szamnak,
 * es innen is lehet hivni.
 */
export function PhoneChannelBar({ channelId, meId }: { channelId: string; meId: string }) {
  const { users } = useUsers();
  const canCallOut = users.find((user) => user.id === meId)?.canCallOut ?? false;
  const callBack = useDialFromChannel(channelId, meId);
  const { data: callable } = useCallableLines(canCallOut);
  const [dialOpen, setDialOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const dialButtonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="relative border-b border-border-soft bg-surface-raised px-4 py-1.5 sm:px-6">
      <div className="flex items-center gap-2">
        {canCallOut ? (
          <button
            ref={dialButtonRef}
            type="button"
            aria-expanded={dialOpen}
            onClick={() => {
              setDialOpen((open) => !open);
              setBookOpen(false);
            }}
            className={`flex items-center gap-1.5 rounded-control px-2.5 py-1 text-xs font-medium transition-colors ${
              dialOpen
                ? 'bg-accent-soft text-accent'
                : 'text-ink-muted hover:bg-surface-hover hover:text-ink'
            }`}
          >
            <Icon name="tarcsazo" size="sm" /> Tárcsázó
          </button>
        ) : (
          <span className="text-xs text-ink-faint">
            Kifelé hívni az admin által adott joggal lehet.
          </span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          aria-expanded={bookOpen}
          onClick={() => {
            setBookOpen((open) => !open);
            setDialOpen(false);
          }}
          className={`flex items-center gap-1.5 rounded-control px-2.5 py-1 text-xs font-medium transition-colors ${
            bookOpen
              ? 'bg-accent-soft text-accent'
              : 'text-ink-muted hover:bg-surface-hover hover:text-ink'
          }`}
        >
          <Icon name="cimjegyzek" size="sm" /> Címjegyzék
        </button>
      </div>

      {callBack.error && <p className="mt-1 text-xs text-danger">{callBack.error}</p>}

      {dialOpen && (
        <Dialer
          triggerRef={dialButtonRef}
          channelId={channelId}
          busy={callBack.busy}
          onDial={(number, lineId) => {
            callBack.dial(number, lineId);
            setDialOpen(false);
          }}
          onClose={() => setDialOpen(false)}
        />
      )}
      {bookOpen && (
        <AddressBook
          canCallOut={canCallOut}
          busy={callBack.busy}
          onDial={(number) => {
            // A cimjegyzekbol az alapertelmezett vonalrol (a csatornae, vagy a legutobbi).
            callBack.dial(number, defaultLineId(callable ?? [], channelId) ?? undefined);
            setBookOpen(false);
          }}
          onClose={() => setBookOpen(false)}
        />
      )}
    </div>
  );
}

/** Szam beirasa es hivasa. A szamszabalyt elore jelezzuk; a dontes a szervere. */
function Dialer({
  triggerRef,
  channelId,
  busy,
  onDial,
  onClose,
}: {
  triggerRef: RefObject<HTMLButtonElement | null>;
  channelId: string;
  busy: boolean;
  onDial: (number: string, lineId?: string) => void;
  onClose: () => void;
}) {
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: lines } = useCallableLines();
  const [lineId, setLineId] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  useDismiss(formRef, onClose, { ignore: [triggerRef], returnFocus: triggerRef });

  return (
    <form
      ref={formRef}
      className="absolute left-4 top-full z-30 mt-1 w-80 rounded-card border border-border bg-surface-raised p-3 shadow-modal sm:left-6"
      onSubmit={(event) => {
        event.preventDefault();
        const check = checkOutboundNumber(number);
        if (!check.ok) {
          setError(check.reason);
          return;
        }
        onDial(check.number, lineId ?? undefined);
      }}
    >
      <label className="text-xs text-ink-muted">
        Hívandó szám
        <input
          autoFocus
          inputMode="tel"
          value={number}
          onChange={(event) => {
            setNumber(event.target.value);
            setError(null);
          }}
          placeholder="06 30 123 4567"
          className="mt-1 w-full rounded-control border border-border bg-surface px-3 py-2 text-base tabular-nums"
        />
      </label>
      <div className="mt-2">
        <LinePicker lines={lines ?? []} value={lineId} onChange={setLineId} channelId={channelId} />
      </div>
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
      {busy && (
        <p className="mt-1.5 text-xs text-ink-muted">
          Egy másik hívásban vagy – előbb lépj ki belőle.
        </p>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || number.trim().length < 3}
          className="flex items-center gap-1.5 rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-accent-ink disabled:opacity-50"
        >
          <Icon name="telefon" size="sm" /> Hívás
        </button>
        <button type="button" onClick={onClose} className="text-xs text-ink-muted hover:text-ink">
          Mégse
        </button>
      </div>
    </form>
  );
}

/**
 * CIMJEGYZEK: a korabbi hivott es hivo szamok, es a nevvel ellatott szamok.
 *
 * A nev a cegen belul kozos. Amint egy szamnak neve van, a hivasoknal, a
 * nem fogadott hivasnal es az atiratban is a nev latszik, mogotte a szam.
 */
function AddressBook({
  canCallOut,
  busy,
  onDial,
  onClose,
}: {
  canCallOut: boolean;
  busy: boolean;
  onDial: (number: string) => void;
  onClose: () => void;
}) {
  const { contacts } = usePhoneContacts();
  const { data: recent, isLoading } = usePhoneRecent(true);
  const [query, setQuery] = useState('');
  // A TT ugyfelei csak TT-s kollegának (lasd lib/session.ts).
  const customerSystem = useCustomerSystem();

  /*
    A LEGUTOBBIAK elol (idorendben), utana a nevvel ellatott, de nalam meg nem
    szerepelt szamok - igy egy kolléga altal felvett nev is elerheto.
  */
  const seen = new Set((recent ?? []).map((item) => item.number));
  const rows: (PhoneRecent | { number: string; name: string; lastAt: null })[] = [
    ...(recent ?? []),
    ...contacts
      .filter((contact) => !seen.has(contact.number))
      .map((contact) => ({ number: contact.number, name: contact.name, lastAt: null })),
  ];
  const needle = query.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const visible = rows.filter(
    (row) =>
      !needle ||
      (row.name ?? '').toLowerCase().includes(needle) ||
      (digits.length > 0 && row.number.replace(/\D/g, '').includes(digits)),
  );

  return (
    <div className="absolute right-4 top-full z-30 mt-1 w-[26rem] max-w-[calc(100vw-2rem)] rounded-card border border-border bg-surface-raised p-3 shadow-modal sm:right-6">
      <div className="mb-2 flex items-center gap-2">
        <input
          aria-label="Keresés a címjegyzékben"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Név vagy szám…"
          className="min-w-0 flex-1 rounded-control border border-border bg-surface px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          className="text-ink-muted hover:text-ink"
        >
          <Icon name="bezaras" />
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-ink-muted">Betöltés…</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-ink-muted">
          {rows.length === 0 ? 'Még nem volt hívás ebben a csatornában.' : 'Nincs találat.'}
        </p>
      ) : (
        <ul className="max-h-96 divide-y divide-border-soft overflow-y-auto">
          {visible.map((row) => (
            <ContactRow
              key={row.number}
              row={row}
              canCallOut={canCallOut}
              busy={busy}
              onDial={onDial}
            />
          ))}
        </ul>
      )}

      {/*
        A TT cimjegyzeke a sajatunk ALATT, kulon szakaszban.

        Nem osszekeverve: a sajat cimjegyzek azt tartalmazza, akivel mar
        beszeltunk vagy akit elneveztunk, a TT pedig az osszes ugyfelet. A ketto
        mast jelent, es a kolléga latja, honnan jon a talalat.
      */}
      {customerSystem === 'tt' && (
        <TtDirectory query={query} canCallOut={canCallOut} busy={busy} onDial={onDial} />
      )}
    </div>
  );
}

function ContactRow({
  row,
  canCallOut,
  busy,
  onDial,
}: {
  row: PhoneRecent | { number: string; name: string; lastAt: null };
  canCallOut: boolean;
  busy: boolean;
  onDial: (number: string) => void;
}) {
  const queryClient = useQueryClient();
  const { nameOf, entryOf } = usePhoneContacts();
  const [editing, setEditing] = useState(false);
  const name = nameOf(row.number) ?? row.name ?? null;
  /** Van-e a szam mogott TT-ugyfel - ha nincs, fel lehet venni oda is. */
  const clinicId = entryOf(row.number)?.ttClinicId ?? null;
  const [draft, setDraft] = useState(name ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      const contacts = await savePhoneContact(row.number, draft.trim());
      queryClient.setQueryData(phoneContactsKey, contacts);
      void queryClient.invalidateQueries({ queryKey: phoneRecentKey });
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A mentés nem sikerült.');
    }
  };

  return (
    <li className="py-2">
      {editing && (
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <input
            aria-label="Névjegy neve"
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Név (üresen hagyva törlődik)…"
            maxLength={80}
            className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2.5 py-1 text-sm"
          />
          <button
            type="submit"
            className="rounded-control bg-accent px-2.5 py-1 text-xs font-semibold text-accent-ink"
          >
            Mentés
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setDraft(name ?? '');
            }}
            className="text-xs text-ink-muted hover:text-ink"
          >
            Mégse
          </button>
        </form>
      )}

      {/*
        UGYFELHEZ KOTES a cimjegyzekbol.

        A puszta nev NEM jut el a TT-be, mert ott nincs hova tenni: a szam egy
        UGYFELHEZ tartozik, nem magaban all. Amint megvan az ugyfel, a mentes
        fel is kuldi (`telefonszam_hozzaadas`), es onnantol a TT is ismeri.
      */}
      {editing && !clinicId && (
        <div className="mt-1.5">
          <AssignCustomer
            number={row.number}
            name={draft.trim() || name}
            onDone={() => {
              void queryClient.invalidateQueries({ queryKey: phoneContactsKey });
              setEditing(false);
            }}
          />
        </div>
      )}

      {!editing && (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">
              {name ? (
                <>
                  <span className="font-medium">{name}</span>{' '}
                  <span className="tabular-nums text-ink-muted">
                    ({formatPhoneNumber(row.number)})
                  </span>
                </>
              ) : (
                <span className="font-medium tabular-nums">{formatPhoneNumber(row.number)}</span>
              )}
            </p>
            {row.lastAt && 'calls' in row && (
              <p className="truncate text-2xs text-ink-faint">
                {row.lastDirection === 'outbound' ? 'Kimenő' : 'Bejövő'} · {formatDay(row.lastAt)}{' '}
                {formatTime(row.lastAt)}
                {row.calls > 1 ? ` · ${row.calls} hívás` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setDraft(name ?? '');
              setEditing(true);
            }}
            title={name ? 'Név módosítása' : 'Név megadása'}
            className="shrink-0 rounded-control px-1.5 py-1 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            <Icon name="szerkesztes" size="sm" /> {name ? '' : 'Név'}
          </button>
          {canCallOut && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onDial(row.number)}
              title="Hívás"
              className="shrink-0 rounded-control bg-accent-soft px-2 py-1 text-xs font-medium text-accent hover:bg-accent hover:text-accent-ink disabled:opacity-50"
            >
              <Icon name="telefon" size="sm" />
            </button>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </li>
  );
}
